import { datadogRum } from '@datadog/browser-rum'
import { ApolloError } from '@apollo/client'
import { DefaultModalContainerNames } from '../../consts/defaultConfigValues/defaultConstants'
import useActivateDeferredPromotion from '../../graphQL/mutations/activateDeferredPromotion/hook'
import {
  IActivateDeferredPromotionResponse,
  IActivateDeferredPromotionData,
} from '../../graphQL/mutations/activateDeferredPromotion/types'
import {
  setAvailableFreegoods,
  setQuantitySelection,
  setSynchronizingCart,
} from '../../redux/slices/catalog'
import {
  getAlertErrorMessage,
  AlertMessageType,
} from '../../utils/getAlertErrorMessage'
import { onCartResponse } from '../../utils/onCartResponse'
import { noteCartWriteSettled } from '../../utils/settledCartWrites'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import useModal from '../useModal'
import { addAlert } from '../../redux/slices/config'
import { generateDataDogContext } from '../../utils/dataDog'
import { PromotionSelectionCriteria } from '../../graphQL/commons'

export const useChosenFreeGoods = () => {
  const { closeModal } = useModal()
  const dispatch = useAppDispatch()
  const store = useAppStore()

  const availableFreegoods = useAppSelector(
    (stateRedux) => stateRedux.catalogSlice.freegoodsConf?.freegoods || [],
  )

  const promotionUid = useAppSelector(
    (stateRedux) => stateRedux.catalogSlice.freegoodsConf?.promotion?.id || '',
  )
  const state = useAppSelector((stateRedux) => stateRedux)
  const { config } = state.defaultSlice
  const sessionUid = useAppSelector(
    (stateRedux) => stateRedux.defaultSlice.sessionId || '',
  )
  const storefrontName = useAppSelector(
    (stateRedux) => stateRedux.defaultSlice.storeName || '',
  )
  const selectByState = useAppSelector(
    (stateRedux) => stateRedux.catalogSlice.freegoodsConf?.selectBy,
  )
  const quantitySelection = useAppSelector(
    (stateRedux) => stateRedux.catalogSlice.freegoodsConf?.quantitySelection,
  )

  const activeModalContainer =
    selectByState === PromotionSelectionCriteria.QUANTITY_SELECTION
      ? DefaultModalContainerNames.CHOSEN_FREEGOODS_QUANTITY
      : DefaultModalContainerNames.CHOSEN_FREEGOODS

  /*
   * Reads the store when the response lands, not the render-time snapshot.
   * Nothing holds the page while the confirm is in flight, so the shopper
   * can go back or navigate elsewhere before it answers, and Apollo still
   * calls this on the unmounted instance. Closing the modal is not such a
   * case: it does not unmount this hook.
   *
   * A snapshot from a page that has since unmounted still holds that page's
   * lists, and `onCartResponse` would write them back over the next page's:
   * among them the catalogue list, the open product and the category-nav
   * list. Which way the category-nav list goes wrong depends on the old page.
   * One that never loads it, such as the cart, holds it as `null`, so the
   * next page's list is skipped rather than updated; a category-nav page has
   * it refilled by `useGetProductsNav`, so its own list is written back over
   * the next page's. See `utils/onCartResponse.ts` for the full set, and
   * `useDeleteProductFromCart` (`useDeleteItemFromCart.ts`) for the same
   * hazard.
   */
  const onCompleted = (data: IActivateDeferredPromotionResponse) => {
    // Counted before the cart is written, success or not, so a refresh
    // whose cart read is out reads it again (COM-1512)
    noteCartWriteSettled()
    if (data.activateDeferredPromotion) {
      onCartResponse({
        data: data.activateDeferredPromotion,
        state: store.getState(),
        dispatch,
      })
      closeModal({ container: activeModalContainer })
    }
  }

  const onError = (error: ApolloError) => {
    noteCartWriteSettled()
    const message = getAlertErrorMessage(
      error.networkError ? AlertMessageType.Network : AlertMessageType.AddItem,
      config?.texts,
    )
    dispatch(addAlert({ message }))
    const context = generateDataDogContext({
      title: 'Add product mutation error',
      extraInfo: { function: 'useAddItemToCart > onError' },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  const [activateDeferredPromotion] = useActivateDeferredPromotion({
    onError,
    onCompleted,
  })

  const chosenFreegoods = ({ id }: { id: string }) => {
    try {
      dispatch(setSynchronizingCart(true))

      const newAvailableFreegoods = availableFreegoods.map((item) => ({
        ...item,
        isChecked: item.id === id,
      }))
      dispatch(
        setAvailableFreegoods({
          freegoods: newAvailableFreegoods,
          isConfirmDisabled: !newAvailableFreegoods.some(
            ({ isChecked }) => isChecked,
          ),
        }),
      )
    } finally {
      dispatch(setSynchronizingCart(false))
    }
  }

  const confirmChosenFreegoods = async ({
    selectBy,
  }: {
    selectBy: PromotionSelectionCriteria
  }) => {
    const selectedGroup = availableFreegoods.find((item) => item.isChecked)

    const selectedSkuQuantities =
      quantitySelection?.products
        ?.filter(({ quantity }) => quantity > 0)
        .map(({ sku, quantity }) => ({
          sku,
          quantity,
        })) || []

    const requiresCustomerSelection =
      selectBy === PromotionSelectionCriteria.CUSTOMER_SELECTION

    if (requiresCustomerSelection && !selectedGroup) {
      return
    }

    const requiresQuantitySelection =
      selectBy === PromotionSelectionCriteria.QUANTITY_SELECTION

    if (requiresQuantitySelection && selectedSkuQuantities.length === 0) {
      return
    }

    const selectedGroupIdentifier = (() => {
      if (requiresCustomerSelection) {
        return selectedGroup?.id ?? null
      }
      if (requiresQuantitySelection) {
        return quantitySelection?.groups?.[0]?.identifier ?? null
      }
      return null
    })()

    const promotionData: IActivateDeferredPromotionData = {
      selectBy,
      promotionUid,
      selectedGroupIdentifier: selectedGroupIdentifier || undefined,
    }

    if (requiresQuantitySelection && selectedSkuQuantities.length > 0) {
      promotionData.skuQuantitySelections = selectedSkuQuantities
    }

    await activateDeferredPromotion({
      variables: {
        sessionUid,
        storefrontName,
        data: promotionData,
      },
    })
  }

  const updateQuantitySelection = ({
    sku,
    quantity,
  }: {
    sku: string
    quantity: number
  }) => {
    if (!quantitySelection) {
      return
    }

    const { products: quantitySelectionProducts, maxTotalQuantity } =
      quantitySelection

    const updatedQuantitySelectionProducts = quantitySelectionProducts.map(
      (product) => {
        if (product.sku === sku) {
          return {
            ...product,
            quantity,
          }
        }
        return product
      },
    )

    const totalSelected = updatedQuantitySelectionProducts.reduce(
      (acc, { quantity }) => acc + quantity,
      0,
    )

    const updatedQuantitySelection = {
      ...quantitySelection,
      products: updatedQuantitySelectionProducts,
      totalSelected,
    }

    dispatch(
      setQuantitySelection({
        quantitySelection: updatedQuantitySelection,
        isConfirmDisabled: totalSelected !== maxTotalQuantity,
      }),
    )
  }

  return { chosenFreegoods, confirmChosenFreegoods, updateQuantitySelection }
}
